import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, RotateCcw, Shield, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

const scenarios = [
  {
    id: "safe-read",
    label: "Safe Dashboard Read",
    description: "Agent reads quarterly revenue → renders dashboard for authorized user",
    steps: [
      { tool: "db.revenue.find()", type: "READ", classification: "CONFIDENTIAL", target: "Internal DB", taint: "CONFIDENTIAL" },
      { tool: "dashboard.render()", type: "MUTATION", classification: "INTERNAL", target: "Employee UI", taint: "CONFIDENTIAL" },
    ],
    verdict: "ALLOW",
    reason: "Mutation target (employee dashboard) matches data owner's clearance level. Taint contained within authorized boundary.",
    riskScore: 0.08,
  },
  {
    id: "suspicious-exfil",
    label: "Suspicious Exfiltration",
    description: "Agent reads salary data → attempts to email summary to external recipient",
    steps: [
      { tool: "hr.salaries.find()", type: "READ", classification: "SECRET", target: "HR Database", taint: "SECRET" },
      { tool: "llm.summarize()", type: "PROCESS", classification: "DERIVED", target: "Context Window", taint: "SECRET" },
      { tool: "email.send(external)", type: "MUTATION", classification: "EXTERNAL", target: "External Email", taint: "SECRET" },
    ],
    verdict: "BLOCK",
    reason: "SECRET-tainted data flowing to EXTERNAL mutation target. Gradient Boosted classifier confidence: 99.7%. Action chain blocked.",
    riskScore: 0.97,
  },
  {
    id: "multi-step-launder",
    label: "Multi-Step Data Laundering",
    description: "Agent reads PII → writes to scratch → re-reads (untainted?) → sends via API",
    steps: [
      { tool: "users.find({pii: true})", type: "READ", classification: "SECRET", target: "User DB", taint: "SECRET" },
      { tool: "scratch.write(data)", type: "MUTATION", classification: "INTERNAL", target: "Temp Storage", taint: "SECRET" },
      { tool: "scratch.read()", type: "READ", classification: "INTERNAL", target: "Temp Storage", taint: "SECRET*" },
      { tool: "api.webhook(payload)", type: "MUTATION", classification: "EXTERNAL", target: "External API", taint: "SECRET*" },
    ],
    verdict: "BLOCK",
    reason: "Taint persistence detected across storage hop. Isolation Forest anomaly score: 0.94. Multi-step laundering pattern identified.",
    riskScore: 0.94,
  },
  {
    id: "safe-internal",
    label: "Safe Internal Report",
    description: "Agent reads metrics → generates internal Slack summary for team lead",
    steps: [
      { tool: "metrics.aggregate()", type: "READ", classification: "INTERNAL", target: "Metrics DB", taint: "INTERNAL" },
      { tool: "llm.summarize()", type: "PROCESS", classification: "DERIVED", target: "Context Window", taint: "INTERNAL" },
      { tool: "slack.post(#team)", type: "MUTATION", classification: "INTERNAL", target: "Internal Slack", taint: "INTERNAL" },
    ],
    verdict: "ALLOW",
    reason: "INTERNAL-tainted data flowing to INTERNAL mutation target within same authorization boundary. Risk score: 0.05.",
    riskScore: 0.05,
  },
];

function TerminalLine({ text, color = "#94A3B8", delay = 0, prefix = "$" }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  if (!visible) return null;
  return (
    <motion.div
      initial={{ opacity: 0, x: -5 }}
      animate={{ opacity: 1, x: 0 }}
      className="font-mono text-xs leading-6 flex"
    >
      <span className="text-[#475569] mr-2 select-none">{prefix}</span>
      <span style={{ color }}>{text}</span>
    </motion.div>
  );
}

function RiskBar({ score, verdict }) {
  const color = verdict === "ALLOW" ? "#10B981" : "#EF4444";
  return (
    <div className="mt-4">
      <div className="flex justify-between text-xs font-mono mb-1">
        <span className="text-[#475569]">RISK_SCORE</span>
        <span style={{ color }}>{(score * 100).toFixed(1)}%</span>
      </div>
      <div className="h-1.5 bg-[#1A2332] w-full">
        <motion.div
          className="h-full"
          style={{ backgroundColor: color }}
          initial={{ width: 0 }}
          animate={{ width: `${score * 100}%` }}
          transition={{ duration: 1, delay: 0.5 }}
        />
      </div>
    </div>
  );
}

export default function InteractiveDemo() {
  const [active, setActive] = useState(0);
  const [running, setRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [showVerdict, setShowVerdict] = useState(false);

  const scenario = scenarios[active];

  const runDemo = useCallback(() => {
    setRunning(true);
    setCurrentStep(-1);
    setShowVerdict(false);

    let step = 0;
    const interval = setInterval(() => {
      if (step < scenario.steps.length) {
        setCurrentStep(step);
        step++;
      } else {
        setShowVerdict(true);
        setRunning(false);
        clearInterval(interval);
      }
    }, 800);

    return () => clearInterval(interval);
  }, [scenario]);

  const reset = () => {
    setRunning(false);
    setCurrentStep(-1);
    setShowVerdict(false);
  };

  const selectScenario = (i) => {
    setActive(i);
    reset();
  };

  const typeColor = (type) => {
    if (type === "READ") return "#00E5FF";
    if (type === "PROCESS") return "#F59E0B";
    return "#EF4444";
  };

  return (
    <section
      id="demo"
      data-testid="demo-section"
      className="relative py-24 lg:py-32 bg-[#050508]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <span className="text-xs font-mono text-[#00E5FF] uppercase tracking-[0.2em] flex items-center gap-2 mb-4">
            <Play size={14} />
            Interactive Demo
          </span>
          <h2
            data-testid="demo-headline"
            className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white mb-4"
          >
            See the <span className="text-[#00E5FF]">Chain of Events</span>
          </h2>
          <p className="text-base text-[#94A3B8] max-w-2xl leading-relaxed">
            Click a scenario to simulate an agent action chain. Watch our ML classifier 
            analyze each step and render a deterministic verdict in real-time.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-[#1A2332]">
          {/* Left: Scenario selector */}
          <div className="lg:col-span-4 bg-[#0D0F14] border-r border-[#1A2332]">
            <div className="p-4 border-b border-[#1A2332]">
              <span className="text-[10px] font-mono text-[#475569] uppercase tracking-[0.2em]">
                Select Scenario
              </span>
            </div>
            {scenarios.map((s, i) => (
              <button
                key={s.id}
                data-testid={`demo-scenario-${s.id}`}
                onClick={() => selectScenario(i)}
                className={`w-full text-left p-4 border-b border-[#1A2332] transition-all ${
                  active === i
                    ? "bg-[#1A1D24] border-l-2 border-l-[#00E5FF]"
                    : "hover:bg-[#0D0F14]/80"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {s.verdict === "ALLOW" ? (
                    <CheckCircle2 size={12} className="text-[#10B981]" />
                  ) : (
                    <XCircle size={12} className="text-[#EF4444]" />
                  )}
                  <span className="text-xs font-mono font-bold text-white">
                    {s.label}
                  </span>
                </div>
                <p className="text-[11px] text-[#94A3B8] leading-relaxed">
                  {s.description}
                </p>
              </button>
            ))}
          </div>

          {/* Right: Terminal visualization */}
          <div className="lg:col-span-8 bg-[#050508] flex flex-col">
            {/* Terminal header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#1A2332] bg-[#0D0F14]">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#10B981]" />
                <span className="text-[10px] font-mono text-[#475569] ml-2">
                  leapforce-classifier — {scenario.label}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="demo-run-btn"
                  onClick={runDemo}
                  disabled={running}
                  className={`px-3 py-1 text-[10px] font-mono uppercase tracking-wider transition-all ${
                    running
                      ? "bg-[#1A2332] text-[#475569] cursor-not-allowed"
                      : "bg-[#00E5FF] text-[#050508] hover:bg-[#33EAFF]"
                  }`}
                >
                  {running ? "Analyzing..." : "Run"}
                </button>
                <button
                  data-testid="demo-reset-btn"
                  onClick={reset}
                  className="px-3 py-1 text-[10px] font-mono border border-[#1A2332] text-[#94A3B8] hover:border-[#00E5FF] transition-all uppercase tracking-wider"
                >
                  <RotateCcw size={10} />
                </button>
              </div>
            </div>

            {/* Terminal body */}
            <div className="p-6 flex-1 min-h-[350px] overflow-y-auto">
              <AnimatePresence mode="wait">
                {currentStep < 0 && !showVerdict ? (
                  <motion.div
                    key="idle"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex flex-col items-center justify-center h-full text-center"
                  >
                    <Shield size={32} className="text-[#1A2332] mb-4" />
                    <p className="text-xs font-mono text-[#475569]">
                      Select a scenario and press RUN to analyze the action chain
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key={`scenario-${active}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <TerminalLine
                      text={`leapforce analyze --chain "${scenario.label}"`}
                      color="#00E5FF"
                      prefix=">"
                    />
                    <TerminalLine
                      text="Initializing taint tracker..."
                      color="#475569"
                      delay={200}
                      prefix=" "
                    />

                    {scenario.steps.map((step, i) => (
                      <div key={i}>
                        {i <= currentStep && (
                          <motion.div
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="my-3 border border-[#1A2332] p-3"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className="text-[10px] font-mono font-bold px-1.5 py-0.5"
                                style={{
                                  color: typeColor(step.type),
                                  border: `1px solid ${typeColor(step.type)}`,
                                }}
                              >
                                {step.type}
                              </span>
                              <span className="text-xs font-mono text-white">
                                {step.tool}
                              </span>
                            </div>
                            <div className="flex gap-4 text-[10px] font-mono text-[#475569]">
                              <span>
                                classification:{" "}
                                <span className="text-[#94A3B8]">
                                  {step.classification}
                                </span>
                              </span>
                              <span>
                                target:{" "}
                                <span className="text-[#94A3B8]">
                                  {step.target}
                                </span>
                              </span>
                              <span>
                                taint:{" "}
                                <span
                                  style={{
                                    color:
                                      step.taint === "SECRET" || step.taint === "SECRET*"
                                        ? "#EF4444"
                                        : step.taint === "CONFIDENTIAL"
                                        ? "#F59E0B"
                                        : "#10B981",
                                  }}
                                >
                                  {step.taint}
                                </span>
                              </span>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    ))}

                    {showVerdict && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-4"
                      >
                        <div className="h-px bg-[#1A2332] mb-4" />
                        <TerminalLine
                          text="Running GBT classifier on featurized chain..."
                          color="#475569"
                          prefix=" "
                        />
                        <div className="mt-3 flex items-center gap-3">
                          <span className="text-xs font-mono text-[#475569]">
                            VERDICT:
                          </span>
                          <span
                            data-testid={`demo-verdict-${scenario.id}`}
                            className="text-sm font-mono font-bold px-3 py-1"
                            style={{
                              color:
                                scenario.verdict === "ALLOW"
                                  ? "#10B981"
                                  : "#EF4444",
                              border: `1px solid ${
                                scenario.verdict === "ALLOW"
                                  ? "#10B981"
                                  : "#EF4444"
                              }`,
                              boxShadow: `0 0 10px ${
                                scenario.verdict === "ALLOW"
                                  ? "rgba(16,185,129,0.3)"
                                  : "rgba(239,68,68,0.3)"
                              }`,
                            }}
                          >
                            {scenario.verdict === "ALLOW" ? (
                              <span className="flex items-center gap-2">
                                <CheckCircle2 size={14} /> ALLOWED
                              </span>
                            ) : (
                              <span className="flex items-center gap-2">
                                <AlertTriangle size={14} /> BLOCKED
                              </span>
                            )}
                          </span>
                        </div>
                        <p className="text-xs text-[#94A3B8] mt-3 leading-relaxed font-mono">
                          {scenario.reason}
                        </p>
                        <RiskBar
                          score={scenario.riskScore}
                          verdict={scenario.verdict}
                        />
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
